// src/components/common/papers/FileTreePanel.jsx
import React, { useCallback, useEffect, useMemo, useState } from "react";
import Tree from "rc-tree";
import "rc-tree/assets/index.css";
import { getPapersTree } from "../../../api/papers";
import { useParams } from "react-router-dom";
import FolderPermissionDialog from "./FolderPermissionDialog";
import { ChevronRight, ChevronDown, Settings } from "lucide-react";

// 폴더 트리 접기/펼치기 아이콘 렌더링
const renderSwitcherIcon = ({ isLeaf, expanded }) => {
  if (isLeaf) {
    // 파일 노드는 스위쳐 아이콘 없이 여백만 유지
    return <span className="inline-block w-4" />;
  }

  return (
    <span className="mr-1 flex h-5 w-5 items-center justify-center rounded-full border border-slate-200 bg-slate-50 text-slate-500">
      {expanded ? (
        <ChevronDown className="h-3 w-3" /> // 펼쳐진 상태 아이콘
      ) : (
        <ChevronRight className="h-3 w-3" /> // 접힌 상태 아이콘
      )}
    </span>
  );
};

export default function FileTreePanel({
  selectionMode = "single",
  initialCheckedFileIds = [],
  defaultExpanded = false, // 최초 진입 시 폴더를 모두 닫힌 상태로
  onPick,
  onChangeSelected,
}) {
  const [treeData, setTreeData] = useState([]);
  const [rawRoots, setRawRoots] = useState([]);
  const [expandedKeys, setExpandedKeys] = useState([]);
  const [checkedKeys, setCheckedKeys] = useState([]); // ✅ 내부 전용 상태
  const [initialized, setInitialized] = useState(false); // ✅ 초기화 여부
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const { id: projectId } = useParams();

  // 권한 설정 다이얼로그
  const [permDialogOpen, setPermDialogOpen] = useState(false);
  const [targetItem, setTargetItem] = useState(null); // folder or file
  const [targetType, setTargetType] = useState("folder"); // "folder" | "file"

  // ✅ file key("file-123") -> rawFile 매핑 (체크박스 기준 선택 계산용)
  const fileMap = useMemo(() => {
    const map = {};
    const walkFolder = (folder) => {
      (folder.files || []).forEach((f) => {
        map[`file-${f.id}`] = f;
      });
      (folder.children || []).forEach(walkFolder);
    };
    rawRoots.forEach(walkFolder);
    return map;
  }, [rawRoots]);

  const checkable = selectionMode === "multiple";

  // 폴더/파일 트리 변환
  const toRcNode = useCallback((folder) => {
    const childrenFolders = (folder.children || []).map(toRcNode);
    const fileNodes = (folder.files || []).map((f) => ({
      key: `file-${f.id}`,
      title: f.name,
      isLeaf: true,
      rawFile: f,
    }));
    return {
      key: `folder-${folder.id}`,
      title: folder.name,
      isLeaf: false,
      rawFolder: folder, // 원본 폴더 데이터 (권한 정보 포함)
      children: [...childrenFolders, ...fileNodes],
    };
  }, []);

  // 서버 트리 로드
  const loadTree = useCallback(async () => {
    if (!projectId) return;
    setLoading(true);
    try {
      const res = await getPapersTree(projectId);
      const roots = res.data || [];
      setRawRoots(roots);
      const rcRoots = roots.map(toRcNode);
      setTreeData(rcRoots);
      if (defaultExpanded) setExpandedKeys(rcRoots.map((r) => r.key));
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [projectId, toRcNode, defaultExpanded]);

  useEffect(() => {
    loadTree();
  }, [loadTree]);

  // 검색 필터링
  const filteredTreeData = useMemo(() => {
    if (!q.trim()) return treeData;
    const term = q.toLowerCase();

    const filterFolder = (folder) => {
      const childFolders = (folder.children || [])
        .map(filterFolder)
        .filter(Boolean);
      const files = (folder.files || []).filter((f) =>
        f.name.toLowerCase().includes(term)
      );
      const selfMatch = folder.name.toLowerCase().includes(term);
      if (selfMatch || childFolders.length || files.length)
        return { ...folder, children: childFolders, files };
      return null;
    };

    const filtered = rawRoots.map(filterFolder).filter(Boolean);
    return filtered.map(toRcNode);
  }, [q, rawRoots, treeData, toRcNode]);

  // ✅ 초기 체크 상태 한 번만 반영
  useEffect(() => {
    if (checkable && !initialized && initialCheckedFileIds?.length) {
      const initialKeys = initialCheckedFileIds.map((id) => `file-${id}`);
      setCheckedKeys(initialKeys);
      setInitialized(true);
    }
  }, [checkable, initialized, initialCheckedFileIds]);

  const handleOpenPermissions = (e, item, type) => {
    e.stopPropagation(); // 클릭 이벤트 전파 방지
    setTargetItem(item);
    setTargetType(type);
    setPermDialogOpen(true);
  };

  return (
    <div className="p-4 border rounded-xl bg-white h-[600px] flex flex-col">
      <div className="mb-2 flex items-center justify-between">
        <span className="font-semibold text-slate-700">📁 자료실</span>
      </div>

      <input
        value={q}
        onChange={(e) => setQ(e.target.value)}
        placeholder="폴더/파일명 검색"
        className="mb-3 border rounded-lg px-2 py-1 text-sm"
      />

      <div className="flex-1 overflow-auto">
        {loading ? (
          <p className="text-sm text-slate-500">불러오는 중...</p>
        ) : (
          <Tree
            treeData={filteredTreeData}
            expandedKeys={expandedKeys}
            onExpand={setExpandedKeys}
            // 텍스트(라벨) 클릭 시 폴더도 함께 펼쳐지도록
            expandAction="click"
            // rc-tree 기본 아이콘(첫 번째 아이콘)만 사용
            titleRender={(n) => {
              const item = n.isLeaf ? n.rawFile : n.rawFolder;
              const type = n.isLeaf ? "file" : "folder";

              return (
                <div className="flex items-center group w-full justify-between pr-2">
                  {/* 왼쪽: 아이콘 + 텍스트 */}
                  <div className="flex items-center">
                    <span className="ml-1 text-sm">{n.title}</span>
                  </div>

                  {/* 오른쪽: 설정 아이콘 (Hover 시 노출) - 권한 있는 파일만 */}
                  {item && item.can_manage && type === "file" && (
                    <button
                      onClick={(e) => {
                        handleOpenPermissions(e, item, type);
                      }}
                      className="p-0.5 text-slate-400 hover:text-indigo-600 opacity-0 group-hover:opacity-100 transition-opacity"
                      title="권한 설정"
                    >
                      <Settings className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
              );
            }}
            checkable={checkable}
            checkStrictly={false} // ✅ 다중 선택 허용
            checkedKeys={checkedKeys} // ✅ 내부 유지
            // ✅ 체크된 키 목록을 기준으로 실제 파일 목록 생성
            onCheck={(nextCheckedKeys) => {
              setCheckedKeys(nextCheckedKeys);

              const files = nextCheckedKeys
                .filter(
                  (key) => typeof key === "string" && key.startsWith("file-")
                )
                .map((key) => fileMap[key])
                .filter(Boolean);

              console.log("selected", files);

              onChangeSelected?.(files);
            }}
            onSelect={(keys, info) => {
              const node = info?.node;
              // --- 🔥 제목 클릭 시 체크박스도 자동으로 토글 ---
              if (checkable && node.isLeaf && node.rawFile) {
                const fileKey = node.key;

                setCheckedKeys((prev) => {
                  const exists = prev.includes(fileKey);
                  const next = exists
                    ? prev.filter((k) => k !== fileKey)
                    : [...prev, fileKey];

                  // ✅ 토글 후의 checkedKeys 기준으로 선택 파일을 계산
                  const selectedFiles = next
                    .filter(
                      (key) =>
                        typeof key === "string" && key.startsWith("file-")
                    )
                    .map((key) => fileMap[key])
                    .filter(Boolean);

                  onChangeSelected?.(selectedFiles);
                  return next;
                });
                return;
              }

              // 단일 선택 모드 (기존 유지)
              if (!checkable && node.isLeaf && node.rawFile) {
                onPick?.(node.rawFile);
              }
            }}
          />
        )}
      </div>

      {/* 권한 설정 다이얼로그 */}
      {targetItem && (
        <FolderPermissionDialog
          open={permDialogOpen}
          onOpenChange={setPermDialogOpen}
          // type에 따라 prop 분기 전달
          folder={targetType === "folder" ? targetItem : null}
          file={targetType === "file" ? targetItem : null}
          type={targetType}
          onUpdate={() => {
            loadTree(); // 트리 갱신
          }}
        />
      )}
    </div>
  );
}
